import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { ArrowLeft, Calendar, User, Tag } from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const res = await axios.get(`${API}/blog/${slug}`);
      setPost(res.data);
      document.title = `${res.data.title} | Market Mop Blog`;
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
    }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="app-container">
        <Toaster position="top-right" richColors />
        <div className="blog-loading-full">Loading...</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="app-container">
        <Toaster position="top-right" richColors />
        <header className="main-header">
          <Link to="/blog" className="logo-section" style={{ textDecoration: "none" }}>
            <img src="/logo.png" alt="Market Mop" className="blog-header-logo" />
            <div className="logo-text">
              <h1>Market Mop</h1>
              <p>Blog</p>
            </div>
          </Link>
        </header>
        <div className="blog-not-found" data-testid="blog-not-found">
          <h2>Post Not Found</h2>
          <p>The blog post you're looking for doesn't exist.</p>
          <Link to="/blog" className="blog-back-link">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Toaster position="top-right" richColors />

      <header className="main-header">
        <Link to="/" className="logo-section" style={{ textDecoration: "none" }}>
          <img src="/logo.png" alt="Market Mop" className="blog-header-logo" />
          <div className="logo-text">
            <h1>Market Mop</h1>
            <p>Walmart Marketplace Bulk Retirement & CSV Exporter Tool</p>
          </div>
        </Link>
        <div className="header-actions">
          <Link to="/blog" style={{ textDecoration: "none", color: "var(--wm-blue)", fontWeight: 600, fontSize: "0.875rem" }}>Blog</Link>
          <Link to="/" className="header-btn btn-primary" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
            Try Free
          </Link>
        </div>
      </header>

      <main className="blog-post-container" data-testid="blog-post">
        <Link to="/blog" className="blog-back-nav" data-testid="blog-back-link">
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>

        <article className="blog-article">
          <header className="blog-article-header">
            <h1 className="blog-article-title" data-testid="blog-post-title">{post.title}</h1>
            <div className="blog-article-meta">
              <span className="blog-meta-item">
                <Calendar className="w-4 h-4" />
                {new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </span>
              <span className="blog-meta-item">
                <User className="w-4 h-4" />
                {post.author}
              </span>
            </div>
            {post.meta_keywords?.length > 0 && (
              <div className="blog-article-tags">
                <Tag className="w-3.5 h-3.5" />
                {post.meta_keywords.map(kw => (
                  <span key={kw} className="blog-tag">{kw}</span>
                ))}
              </div>
            )}
          </header>

          <div className="blog-article-body" data-testid="blog-post-content" dangerouslySetInnerHTML={{ __html: post.content }} />

          <div className="blog-article-cta">
            <h3>Ready to manage your Walmart inventory?</h3>
            <p>Try Market Mop free — no account required for your first 5 items.</p>
            <Link to="/" className="blog-cta-btn" data-testid="blog-cta-btn">Get Started Free</Link>
          </div>
        </article>
      </main>

      <footer className="blog-footer">
        <div className="blog-footer-inner">
          <Link to="/" style={{ textDecoration: "none" }}>
            <img src="/logo.png" alt="Market Mop" className="blog-footer-logo" />
          </Link>
          <p className="blog-footer-text">MarketMop.com — The fastest way to manage your Walmart Marketplace inventory.</p>
          <div className="blog-footer-links">
            <Link to="/" data-testid="footer-home-link">Home</Link>
            <Link to="/blog" data-testid="footer-blog-link">Blog</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
